<template>
  <div class="delivery-date">
    <div class="delivery-date__title">
      Дата и время
    </div>

    <ui-select
      label="Дата *"
      placeholder="Выберите дату доставки"
      :options="dates"
      :value="dateIdTemp"
      :loading="UPDATE_PROGRESS"
      class="delivery-date__date"
      @change="handleDate"
    />

    <ui-select
      ref="time"
      label="Время *"
      placeholder="Выберите время доставки"
      :options="times"
      :value="timeIdTemp"
      :loading="UPDATE_PROGRESS"
      class="delivery-date__time"
      @change="handleTime"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ddmmyyyy } from '@/utils/date-to-string';
import isToday from '@/utils/is-today';
import isTomorrow from '@/utils/is-tomorrow';

const additionalNameTomorrow = '(Завтра)';
const additionalNameToday = '(Сегодня)';

export default {
  name: 'delivery-date',

  props: {
    value: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    dateTitleFrom: '',
    timeTitle: '',
    dateIdTemp: '',
    timeIdTemp: '',
  }),

  computed: {
    ...mapGetters({
      DELIVERY_INFO: 'cart/deliveryInfo',
      UPDATE_PROGRESS: 'cart/updateProgress',
      GET_DATE_BY_ID: 'cart/getDeliveryDateById',
    }),

    interval() {
      return this.DELIVERY_INFO.interval || [];
    },

    intervalWatchTrigger() {
      return JSON.stringify(this.interval);
    },

    date() {
      return this.interval.find(({ id }) => id === this.dateIdTemp) || {};
    },

    dates() {
      return this.interval.map(interval => {
        const dateFrom = new Date(interval.dateFrom);
        const from = ddmmyyyy(dateFrom, '.');
        const to = interval.dateTo ? ddmmyyyy(new Date(interval.dateTo), '.') : null;

        return {
          name: to ? this.formatIntervalDates(from, to) : `${this.getDateCaption(dateFrom)}`,
          value: interval.id,
        };
      }) || [];
    },

    times() {
      const times = this.date.time?.map(t => ({
        name: t.value,
        value: t.id,
      })) || [];

      times.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });

      return times;
    },

    selectedDate() {
      return this.GET_DATE_BY_ID(this.value);
    },
  },

  watch: {
    // id интервалов меняются после каждого запроса, по этому ищем значения по названию даты и время интервала
    intervalWatchTrigger: {
      handler() {
        this.dateIdTemp = this.getDateByName(this.dateTitleFrom, this.dateTitleTo)?.value;
        this.timeIdTemp = this.getTimeByName(this.timeTitle)?.value;
      },
      immediate: true,
    },
    value: {
      immediate: true,
      handler(val) {
        const selectedDate = this.GET_DATE_BY_ID(val);

        if (!selectedDate) {
          this.dateTitleFrom = '';
          this.timeTitle = '';
          this.dateIdTemp = '';
          this.timeIdTemp = '';
          return;
        }

        this.dateTitleFrom = ddmmyyyy(new Date(selectedDate?.dateFrom), '.');
        if (selectedDate?.dateTo) {
          this.dateTitleTo = ddmmyyyy(new Date(selectedDate?.dateTo), '.');
        }
        this.timeTitle = selectedDate?.interval;
        this.dateIdTemp = selectedDate?.dateId;
        this.timeIdTemp = selectedDate?.intervalId;
      },
    },
  },

  methods: {
    handleDate(date) {
      // учитываем, что из ui-select дата может приходить полноценным интервалом
      let dateFrom = date.name.length > 10 ? date.name.slice(0, 10) : date.name;
      this.dateTitleFrom = this.clearCaption(dateFrom);
      this.dateIdTemp = date.value;

      if (this.timeTitle) {
        this.timeIdTemp = this.getTimeByName(this.timeTitle)?.value;
        this.$emit('input', this.timeIdTemp);
      }
    },

    handleTime(time) {
      this.timeTitle = time.name;
      this.timeIdTemp = time.value;
      this.$emit('input', time.value);
    },

    getDateByName(fromDateName, toDateName) {
      return this.dates.find(date => {
        const dateCaption = toDateName
          ? this.formatIntervalDates(fromDateName, toDateName)
          : fromDateName;

        return dateCaption === this.clearCaption(date.name);
      }) || {};
    },

    getTimeByName(timeName) {
      return this.times.find(({ name }) => timeName === name) || {};
    },

    getDateCaption(date) {
      const caption = isToday(date) ? additionalNameToday : isTomorrow(date)
        ? additionalNameTomorrow
        : false;
      const from = ddmmyyyy(date, '.');

      return caption ? `${from} ${caption}` : `${from}`;
    },

    clearCaption(dateTitle = '') {
      return dateTitle.replace(/\D\(\D+\)/, '');
    },

    formatIntervalDates(fromDate, toDate) {
      return `${fromDate} - ${toDate}`;
    },
  },
};
</script>

<style lang="scss">
.delivery-date {
  &__title {
    margin-bottom: 12px;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: $black;
  }

  &__date {
    margin-bottom: 12px;
  }
}

@include breakpoint("tablet") {
  .delivery-date {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__title {
      width: 161px;
      font-size: 18px;
      line-height: 24px;
      margin: 16px 20px 0 0;
      flex-shrink: 0;
      flex-grow: 1;
    }

    &__date {
      width: 248px;
      margin: 0 26px 0 0;
    }

    &__time {
      width: 249px;
    }
  }
}

@include breakpoint("desktop") {
  .delivery-date {
    &__title {
      flex-grow: 0;
      width: 122px;
      margin-right: 16px;
    }

    &__date {
      width: 214px;
      margin-right: 16px;
    }

    &__time {
      width: 170px;
    }
  }
}
</style>
